import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { NotificationService } from '../services/notification.service';
import { SuperAdminAuthService } from '../services/superadmin-auth.service';
import { LoadingService } from '../services/loading.service';

function extractErrorMessage(error: HttpErrorResponse): string {
  const body = error.error;
  const message = body?.message;
  if (typeof message === 'string' && message.trim()) {
    return message;
  }

  const detail = body?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          return item.msg || item.message || null;
        }
        return null;
      })
      .filter(Boolean);
    if (parts.length) {
      return parts.join(', ');
    }
  }
  if (detail && typeof detail === 'object') {
    return detail.msg || detail.message || 'Request failed';
  }

  return typeof error.message === 'string' ? error.message : '';
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const router = inject(Router);
  const notificationService = inject(NotificationService);
  const superAdminAuthService = inject(SuperAdminAuthService);
  const loadingService = inject(LoadingService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      let errorMessage = 'An error occurred';

      if (error.error instanceof ErrorEvent) {
        // Client-side error
        errorMessage = `Error: ${error.error.message}`;
      } else {
        // Server-side error
        switch (error.status) {
          case 401:
            loadingService.reset();

            // Cookie refresh / OTP endpoints must not wipe a successful Super Admin session.
            if (
              req.url.includes('/auth/refresh') ||
              req.url.includes('/auth/login') ||
              req.url.includes('/superadmin/auth/')
            ) {
              return throwError(() => error);
            }

            const currentUrl = router.url || window.location.pathname;
            const onLogin = currentUrl.includes('/super-admin/login');
            const isSuperAdminContext = currentUrl.includes('/super-admin') ||
                req.url.includes('/superadmin/') ||
                req.url.includes('/admin/system') ||
                req.url.includes('/admin/organizations') ||
                req.url.includes('/admin/users') ||
                req.url.includes('/admin/config') ||
                req.url.includes('/admin/feature-flags');

            if (onLogin) {
              return throwError(() => error);
            }

            if (isSuperAdminContext) {
              superAdminAuthService.clearAuthData();
              errorMessage = 'Session expired. Please login again.';
              router.navigate(['/super-admin/login']);
            } else {
              errorMessage = 'Unauthorized. Please login again.';
              router.navigate(['/super-admin/login']);
            }
            break;
          case 403:
            errorMessage = 'Access denied. Insufficient permissions.';
            break;
          case 404:
            // Don't show error notification for 404s - often expected
            return throwError(() => error);
          case 500:
            errorMessage = 'Internal server error. Please try again later.';
            break;
          default:
            errorMessage = extractErrorMessage(error) || 'An unexpected error occurred';
        }
      }

      notificationService.error(String(errorMessage));
      return throwError(() => error);
    })
  );
};

